import {
    collection,
    doc,
    addDoc,
    getDocs,
    getDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    increment,
    arrayUnion,
    arrayRemove,
  } from "firebase/firestore"
  import { db } from "../firebase/config"
  
  export interface Comment {
    id: string
    productId: string
    userId: string
    userName: string
    userEmail: string
    content: string
    parentId?: string // For nested comments
    likes: string[] // Array of user IDs who liked
    likesCount: number
    replies?: Comment[]
    createdAt: string
    updatedAt: string
  }
  
  export const commentService = {
    // Add a new comment
    async addComment(
      productId: string,
      userId: string,
      userName: string,
      userEmail: string,
      content: string,
      parentId?: string,
    ): Promise<string> {
      try {
        const commentData = {
          productId,
          userId,
          userName,
          userEmail,
          content,
          parentId: parentId || null,
          likes: [],
          likesCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
  
        const docRef = await addDoc(collection(db, "comments"), commentData)
  
        // Update product comment count
        const productRef = doc(db, "products", productId)
        await updateDoc(productRef, {
          commentCount: increment(1),
          updatedAt: new Date().toISOString(),
        })
  
        return docRef.id
      } catch (error) {
        console.error("Error adding comment:", error)
        throw error
      }
    },
  
    // Get comments for a product
    async getComments(productId: string): Promise<Comment[]> {
      try {
        const q = query(collection(db, "comments"), where("productId", "==", productId), orderBy("createdAt", "desc"))
  
        const snapshot = await getDocs(q)
        const comments = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Comment[]
  
        // Organize comments with replies
        const commentMap = new Map<string, Comment>()
        const rootComments: Comment[] = []
  
        // First pass: create comment map
        comments.forEach((comment) => {
          comment.replies = []
          commentMap.set(comment.id, comment)
        })
  
        // Second pass: organize hierarchy
        comments.forEach((comment) => {
          if (comment.parentId) {
            const parent = commentMap.get(comment.parentId)
            if (parent) {
              parent.replies!.push(comment)
            }
          } else {
            rootComments.push(comment)
          }
        })
  
        // Sort replies by creation date (oldest first for better conversation flow)
        rootComments.forEach((comment) => {
          if (comment.replies) {
            comment.replies.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
          }
        })
  
        return rootComments
      } catch (error) {
        console.error("Error getting comments:", error)
        throw error
      }
    },
  
    // Like/unlike a comment
    async toggleCommentLike(commentId: string, userId: string): Promise<boolean> {
      try {
        const commentRef = doc(db, "comments", commentId)
        const commentSnap = await getDoc(commentRef)
  
        if (!commentSnap.exists()) {
          throw new Error("Comment not found")
        }
  
        const comment = commentSnap.data() as Comment
        const isLiked = comment.likes.includes(userId)
  
        if (isLiked) {
          // Unlike
          await updateDoc(commentRef, {
            likes: arrayRemove(userId),
            likesCount: increment(-1),
            updatedAt: new Date().toISOString(),
          })
          return false
        } else {
          // Like
          await updateDoc(commentRef, {
            likes: arrayUnion(userId),
            likesCount: increment(1),
            updatedAt: new Date().toISOString(),
          })
          return true
        }
      } catch (error) {
        console.error("Error toggling comment like:", error)
        throw error
      }
    },
  
    // Update a comment
    async updateComment(commentId: string, content: string): Promise<void> {
      try {
        const commentRef = doc(db, "comments", commentId)
        await updateDoc(commentRef, {
          content,
          updatedAt: new Date().toISOString(),
        })
      } catch (error) {
        console.error("Error updating comment:", error)
        throw error
      }
    },
  
    // Delete a comment
    async deleteComment(commentId: string, productId: string): Promise<void> {
      try {
        // Delete the comment
        await deleteDoc(doc(db, "comments", commentId))
  
        // Update product comment count
        const productRef = doc(db, "products", productId)
        await updateDoc(productRef, {
          commentCount: increment(-1),
          updatedAt: new Date().toISOString(),
        })
      } catch (error) {
        console.error("Error deleting comment:", error)
        throw error
      }
    },
  }
  